import { formatDating, datingSearchUrl } from '../utils/dating';
import React, { useEffect, useState } from 'react';
import { Edit3 } from 'lucide-react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { fetchDetailById } from '../api';
import CustomDescription from '../components/CustomDescription';
import LinkedText from '../components/LinkedText';
import RecordPhotoGrid from '../components/RecordPhotoGrid';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../hooks/useLanguage';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

const copy = {
  RU: {
    back: 'Каталог', kind: 'ДЕТАЛЬ', untitled: 'Без названия', id: 'ID детали', type: 'Тип', subtype: 'Разновидность',
    number: 'Серийный номер', year: 'Год выпуска', category: 'Назначение', maker: 'Производитель', product: 'Изделие',
    description: 'Описание', source: 'Источник', stamps: 'Клейма', photographs: 'Фотографии', noPhotos: 'Фотографий нет.',
    openPhoto: 'Открыть', photoInfo: 'Информация о фото', edit: 'Редактировать запись', loading: 'Загрузка записи…',
    error: 'Не удалось загрузить запись.',
  },
  EN: {
    back: 'Catalogue', kind: 'PART', untitled: 'Untitled', id: 'Part ID', type: 'Type', subtype: 'Subtype',
    number: 'Serial number', year: 'Production year', category: 'Category', maker: 'Maker', product: 'Example',
    description: 'Description', source: 'Source', stamps: 'Marks', photographs: 'Photographs', noPhotos: 'No photographs.',
    openPhoto: 'Open', photoInfo: 'Photo information', edit: 'Edit record', loading: 'Loading record…',
    error: 'Unable to load the record.',
  },
};

function searchUrl(key, value) {
  const filterable = ['type', 'subtype', 'year', 'belongsTo', 'manufacturer', 'stamps'].includes(key);
  return '/search?' + new URLSearchParams(filterable
    ? { searchType: key === 'subtype' ? 'details' : 'all', [key]: String(value) }
    : { searchType: 'all', q: String(value) }).toString();
}

export default function DetailPage() {
  const { id } = useParams();
  const { state } = useLocation();
  const catalogReturnTo = state?.catalogReturnTo || '/search';
  const catalogReturnKey = state?.catalogReturnKey;
  const { currentUser } = useAuth();
  const { lang } = useLanguage();
  const labels = copy[lang] || copy.RU;
  const [detail, setDetail] = useState(null);
  useDocumentTitle(detail?.name || detail?.type?.name);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    let active = true;
    const cacheKey = 'detail:' + id;
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      try { setDetail(JSON.parse(cached)); } catch {}
    }
    setStatus('loading');
    fetchDetailById(id)
      .then(({ data }) => {
        if (!active) return;
        setDetail(data);
        sessionStorage.setItem(cacheKey, JSON.stringify(data));
        setStatus('ready');
      })
      .catch(() => active && setStatus('error'));
    return () => { active = false; };
  }, [id]);

  useEffect(() => {
    const scrollY = Number(sessionStorage.getItem('detailScroll:' + id) || 0);
    window.scrollTo(0, scrollY);
    return () => sessionStorage.setItem('detailScroll:' + id, String(window.scrollY));
  }, [id]);

  if (status === 'error' && !detail) return <main className="record-state page-width">{labels.error}</main>;
  if (!detail) return <main className="record-state page-width">{labels.loading}</main>;

  const facts = [
    detail.type?.name && [labels.type, <Link to={searchUrl('type', detail.type.name)}>{detail.type.name}</Link>],
    detail.subtype?.name && [labels.subtype, <Link to={searchUrl('subtype', detail.subtype.name)}>{detail.subtype.name}</Link>],
    detail.number && [labels.number, <Link to={searchUrl('q', detail.number)}>{detail.number}</Link>],
    formatDating(detail, lang) && [labels.year, <Link to={datingSearchUrl(detail)}>{formatDating(detail, lang)}</Link>],
    detail.belongsTo && [labels.category, <Link to={searchUrl('belongsTo', detail.belongsTo)}>{detail.belongsTo}</Link>],
    detail.manufacturer?.name && [labels.maker, <Link to={'/manufacturers/' + detail.manufacturer.id}>{detail.manufacturer.name}</Link>],
    detail.product?.id && [labels.product, <Link to={'/products/' + detail.product.id} state={{ catalogReturnTo, catalogReturnKey }}>{detail.product.name || '#' + detail.product.id}</Link>],
  ].filter(Boolean);

  return (
    <main className="record-page page-width">
      <header className="record-header">
        <div className="record-breadcrumb"><Link to={catalogReturnTo} state={{ restoreScrollKey: catalogReturnKey }}>← {labels.back}</Link><span>/</span><span>{labels.kind} {String(detail.id).padStart(5, '0')}</span></div>
        <div className="record-title-row">
          <div><p className="section-kicker">{labels.kind}</p><h1>{detail.name || detail.type?.name || labels.untitled}</h1><small>{labels.id}: {detail.id}</small></div>
          {currentUser && <Link to={'/details/' + detail.id + '/edit'} className="secondary-button"><Edit3 size={16} />{labels.edit}</Link>}
        </div>
      </header>

      <div className="record-layout">
        <div className="record-main">
          {facts.length > 0 && <dl className="record-fact-sheet">{facts.map(([term, value]) => <React.Fragment key={term}><dt>{term}</dt><dd>{value}</dd></React.Fragment>)}</dl>}

          {detail.description && <section className="record-section"><h2>{labels.description}</h2><CustomDescription html={detail.description} /></section>}
          {detail.source && <section className="record-section"><h2>{labels.source}</h2><div className="record-source"><LinkedText>{detail.source}</LinkedText></div></section>}

          {detail.stamps?.length > 0 && (
            <section className="record-section">
              <h2>{labels.stamps}<span>{detail.stamps.length}</span></h2>
              <div className="record-tags">{detail.stamps.map((stamp) => <Link to={searchUrl('stamps', stamp.name)} key={stamp.id || stamp.name}>{stamp.name}</Link>)}</div>
            </section>
          )}
        </div>

        <aside className="record-aside"><h2>{labels.photographs}<span>{detail.photos?.length || 0}</span></h2><RecordPhotoGrid photos={detail.photos || []} labels={{ empty: labels.noPhotos, open: labels.openPhoto, info: labels.photoInfo }} /></aside>
      </div>
    </main>
  );
}
